import { useEffect } from 'react'
import { createPortal } from 'react-dom'
import { useQuery } from '@tanstack/react-query'
import { type WorkItemDependency, releaseApi } from '../api'

interface Props {
    workItemId: number | null
    onClose: () => void
}

function StateBadge({ state }: { state: string }) {
    const colours: Record<string, string> = {
        New:      'bg-slate-700 text-slate-300',
        Active:   'bg-blue-900/60 text-blue-300',
        Resolved: 'bg-green-900/60 text-green-300',
        Closed:   'bg-slate-700/50 text-slate-500',
    }
    const cls = colours[state] ?? 'bg-slate-700 text-slate-300'
    return (
        <span className={`text-[10px] font-semibold rounded-full px-2.5 py-0.5 ${cls}`}>
            {state}
        </span>
    )
}

function DependencyChip({ dep }: { dep: WorkItemDependency }) {
    const stateColour: Record<string, string> = {
        New:      'text-slate-400',
        Active:   'text-blue-400',
        Resolved: 'text-green-400',
        Closed:   'text-slate-600',
    }
    return (
        <div className="flex items-center gap-2 py-0.5">
            <span className="font-mono text-[10px] text-indigo-400 shrink-0">#{dep.id}</span>
            <span className="text-xs text-slate-300 flex-1 min-w-0 truncate">{dep.title}</span>
            <span className={`text-[10px] shrink-0 ${stateColour[dep.state] ?? 'text-slate-500'}`}>{dep.state}</span>
        </div>
    )
}

function HtmlContent({ html }: { html: string }) {
    return (
        <div
            className="prose prose-invert prose-sm max-w-none text-slate-300
                [&_p]:my-1.5 [&_ul]:my-1.5 [&_li]:my-0.5 [&_h1]:text-base
                [&_h2]:text-sm [&_h3]:text-sm [&_a]:text-indigo-400
                [&_table]:text-xs [&_td]:p-1 [&_th]:p-1 [&_code]:text-indigo-300"
            // biome-ignore lint/security/noDangerouslySetInnerHtml: ADO returns trusted HTML
            dangerouslySetInnerHTML={{ __html: html }}
        />
    )
}

export function WorkItemDetailModal({ workItemId, onClose }: Props) {
    const { data, isLoading, isError } = useQuery({
        queryKey: ['work-item-detail', workItemId],
        queryFn: () => releaseApi.getWorkItemDetail(workItemId!),
        enabled: workItemId !== null,
        staleTime: 60_000,
    })

    // Dismiss on Escape
    useEffect(() => {
        const handler = (e: KeyboardEvent) => { if (e.key === 'Escape') { onClose() } }
        window.addEventListener('keydown', handler)
        return () => window.removeEventListener('keydown', handler)
    }, [onClose])

    if (workItemId === null) { return null }

    return createPortal(
        <div
            className="fixed inset-0 z-50 flex items-center justify-center p-4"
            onClick={onClose}
        >
            {/* Backdrop */}
            <div className="absolute inset-0 bg-black/60 backdrop-blur-sm" />

            {/* Panel */}
            <div
                className="relative z-10 w-full max-w-2xl max-h-[85vh] flex flex-col
                    rounded-2xl border border-slate-700 bg-slate-900 shadow-2xl overflow-hidden"
                onClick={e => e.stopPropagation()}
            >
                {/* Header */}
                <div className="flex items-start gap-3 px-6 py-4 border-b border-slate-800">
                    {isLoading || !data ? (
                        <div className="flex-1 h-5 rounded-md bg-slate-700 animate-pulse" />
                    ) : (
                        <>
                            <div className="flex-1 min-w-0">
                                <div className="flex items-center gap-2 mb-1 flex-wrap">
                                    <span className="text-xs font-mono text-slate-500">AB#{data.id}</span>
                                    <span className="text-[10px] font-semibold bg-slate-700 rounded-full px-2 py-0.5 text-slate-300">
                                        {data.workItemType}
                                    </span>
                                    <StateBadge state={data.state} />
                                    {data.assignedTo && (
                                        <span className="text-[10px] text-slate-500">{data.assignedTo}</span>
                                    )}
                                </div>
                                <h2 className="text-sm font-semibold text-slate-100 leading-snug">
                                    {data.title}
                                </h2>
                                {data.tags && (
                                    <p className="mt-1 text-[10px] text-slate-600 font-mono">{data.tags}</p>
                                )}
                                {data.createdBy && (
                                    <p className="mt-1 text-[10px] text-slate-500">
                                        Raised by <span className="text-slate-400">{data.createdBy}</span>
                                    </p>
                                )}
                            </div>
                        </>
                    )}
                    <button
                        onClick={onClose}
                        className="shrink-0 rounded-lg p-1.5 text-slate-400 hover:text-slate-200 hover:bg-slate-800 transition-colors"
                        title="Close"
                    >
                        <svg className="h-4 w-4" viewBox="0 0 16 16" fill="none" stroke="currentColor" strokeWidth="2">
                            <path d="M4 4l8 8M12 4l-8 8" />
                        </svg>
                    </button>
                </div>

                {/* Body */}
                <div className="flex-1 overflow-y-auto px-6 py-4 flex flex-col gap-5">
                    {isLoading && (
                        <div className="space-y-2">
                            <div className="h-3 rounded bg-slate-700 animate-pulse w-3/4" />
                            <div className="h-3 rounded bg-slate-700 animate-pulse w-1/2" />
                            <div className="h-3 rounded bg-slate-700 animate-pulse w-5/6" />
                        </div>
                    )}

                    {isError && (
                        <p className="text-red-400 text-sm">Failed to load work item details.</p>
                    )}

                    {data && (
                        <>
                            {data.description && (
                                <section>
                                    <h3 className="text-[10px] font-semibold uppercase tracking-widest text-slate-500 mb-2">
                                        Description
                                    </h3>
                                    <HtmlContent html={data.description} />
                                </section>
                            )}

                            {data.acceptanceCriteria && (
                                <section>
                                    <h3 className="text-[10px] font-semibold uppercase tracking-widest text-slate-500 mb-2">
                                        Acceptance Criteria
                                    </h3>
                                    <HtmlContent html={data.acceptanceCriteria} />
                                </section>
                            )}

                            {!data.description && !data.acceptanceCriteria && (
                                <p className="text-slate-600 text-sm italic">No description or acceptance criteria.</p>
                            )}

                            {((data.requires?.length ?? 0) > 0 || (data.requiredBy?.length ?? 0) > 0) && (
                                <section>
                                    <h3 className="text-[10px] font-semibold uppercase tracking-widest text-slate-500 mb-2">
                                        Dependencies
                                    </h3>
                                    <div className="space-y-2">
                                        {(data.requires?.length ?? 0) > 0 && (
                                            <div>
                                                <p className="text-[10px] text-slate-600 mb-1">Requires (this item depends on)</p>
                                                {data.requires!.map(dep => (
                                                    <DependencyChip key={dep.id} dep={dep} />
                                                ))}
                                            </div>
                                        )}
                                        {(data.requiredBy?.length ?? 0) > 0 && (
                                            <div>
                                                <p className="text-[10px] text-slate-600 mb-1">Required by (these items depend on this)</p>
                                                {data.requiredBy!.map(dep => (
                                                    <DependencyChip key={dep.id} dep={dep} />
                                                ))}
                                            </div>
                                        )}
                                    </div>
                                </section>
                            )}

                            {data.specHub && (
                                <section className="rounded-lg border border-indigo-800/40 bg-indigo-950/30 px-4 py-3">
                                    <div className="flex items-center gap-2 mb-2">
                                        <h3 className="text-[10px] font-semibold uppercase tracking-widest text-indigo-400">
                                            SpecHub Provenance
                                        </h3>
                                        <a
                                            href={`https://spechub.coravant.app`}
                                            target="_blank"
                                            rel="noreferrer"
                                            className="ml-auto text-[10px] text-indigo-500 hover:text-indigo-300 transition-colors"
                                        >
                                            View in SpecHub ↗
                                        </a>
                                    </div>
                                    <div className="space-y-1 text-xs text-slate-400">
                                        <p>
                                            <span className="text-slate-500">Authored by </span>
                                            <span className="text-slate-300">{data.specHub.authorEmail.split('@')[0]}</span>
                                        </p>
                                        <p>
                                            <span className="text-slate-500">Approved &amp; promoted by </span>
                                            <span className="text-slate-300">{data.specHub.promotedByEmail.split('@')[0]}</span>
                                            <span className="text-slate-500"> · </span>
                                            <span className="text-slate-500">{new Date(data.specHub.promotedAt).toLocaleDateString()}</span>
                                        </p>
                                    </div>
                                </section>
                            )}

                            {data.specHubBody && (
                                <section>
                                    <h3 className="text-[10px] font-semibold uppercase tracking-widest text-slate-500 mb-2">
                                        Spec
                                    </h3>
                                    <pre className="rounded-lg bg-slate-800/60 border border-slate-700/60 px-4 py-3 text-xs text-slate-300 whitespace-pre-wrap font-mono leading-relaxed overflow-x-auto">
                                        {data.specHubBody.bodyMd}
                                    </pre>
                                </section>
                            )}

                            {data.specHubBody !== undefined && data.specHub && (
                                <section>
                                    <div className="flex items-center gap-2 mb-2">
                                        <h3 className="text-[10px] font-semibold uppercase tracking-widest text-slate-500">
                                            Test Script
                                        </h3>
                                        <span className="text-[9px] rounded-full bg-slate-700/60 px-2 py-0.5 text-slate-500">Coming soon</span>
                                    </div>
                                    <p className="text-xs text-slate-600 italic">
                                        No test script has been uploaded for this spec yet. Manage test scripts in the Ops portal test scripts section.
                                    </p>
                                </section>
                            )}
                        </>
                    )}
                </div>
            </div>
        </div>,
        document.body
    )
}
